// First Created - 13 Sep 2026
// Author - Atul Pandey
// Purpose - Present aggregated Trinity Intention Board response insights
//           across candidates and questions.
// Updated - 13 Sep 2026 - professionalized insights presentation
// Updated - 13 Sep 2026 - tiled Candidate Responsiveness board
// Updated - 13 Sep 2026 - submission rank based on complete response order
// Updated - 13 Sep 2026 - responsive candidate tile grid
//                         and submitted / not submitted visual treatment
// Updated - 13 Sep 2026 - added Not Submitted badge
//                         and fixed Questions navigator with scrolling response pane

import {
  useMemo,
  useRef,
  useState,
} from "react"

import {
  ChartBarIcon,
  UserGroupIcon,
  UserIcon,
  QuestionMarkCircleIcon,
  BoltIcon,
  ClockIcon,
  CheckCircleIcon,
  CalendarDaysIcon,
  ArrowTrendingUpIcon,
} from "@heroicons/react/24/outline"

import PulseLoader
  from "react-spinners/PulseLoader"

import {
  useGetOrgIntentionResponseInsightsQuery,
} from "@/features/orgIntentionBoards/orgIntentionResponsesApiSlice"


const EtOrgIntentionResponseInsights =
  ({
    intentionBoardId,
  }) => {

    /*
    |--------------------------------------------------------------------------
    | 01 - Local State
    |--------------------------------------------------------------------------
    */

    const [
      activeView,
      setActiveView,
    ] =
      useState(
        "overview"
      )

    const [
      selectedQuestionId,
      setSelectedQuestionId,
    ] =
      useState(
        null
      )

    const [
      showAllCandidates,
      setShowAllCandidates,
    ] =
      useState(
        false
      )

    const [
      selectedInsightCandidateId,
      setSelectedInsightCandidateId,
    ] =
      useState(
        null
      )

    
    /*
    |--------------------------------------------------------------------------
    | Questions Response Pane Ref
    |--------------------------------------------------------------------------
    */

    const questionResponsePaneRef =
    useRef(
        null
    )


    /*
    |--------------------------------------------------------------------------
    | 02 - Response Insights
    |--------------------------------------------------------------------------
    */

    const {
      data:
        responseInsights,

      isLoading,

      isFetching,

      isError,

      error,
    } =
      useGetOrgIntentionResponseInsightsQuery(
        {
          intentionBoardId,
        },
        {
          skip:
            !intentionBoardId,
        }
      )


    /*
    |--------------------------------------------------------------------------
    | 03 - Data
    |--------------------------------------------------------------------------
    */

    const summary =
      responseInsights
        ?.summary ||
      {}

    const candidates =
      responseInsights
        ?.candidates ||
      []

    const questions =
      responseInsights
        ?.questions ||
      []


    /*
    |--------------------------------------------------------------------------
    | 04 - Submitted Candidates
    |--------------------------------------------------------------------------
    */

    const submittedCandidates =
      useMemo(
        () => {

          return candidates
            .filter(
              candidate =>
                candidate
                  ?.responsiveness
                  ?.submissionRank
            )
            .slice()
            .sort(
              (
                a,
                b
              ) =>
                a
                  .responsiveness
                  .submissionRank -
                b
                  .responsiveness
                  .submissionRank
            )

        },
        [
          candidates,
        ]
      )


    /*
    |--------------------------------------------------------------------------
    | 05 - Candidate Board Ordering
    |--------------------------------------------------------------------------
    |
    | Ranked submitted candidates first.
    |
    | Remaining candidates follow alphabetically.
    |
    */

    const responsivenessCandidates =
      useMemo(
        () => {

          return [
            ...candidates,
          ]
            .sort(
              (
                a,
                b
              ) => {

                const rankA =
                  a
                    ?.responsiveness
                    ?.submissionRank

                const rankB =
                  b
                    ?.responsiveness
                    ?.submissionRank

                /*
                |--------------------------------------------------------------------------
                | Both Ranked
                |--------------------------------------------------------------------------
                */

                if (
                  rankA &&
                  rankB
                ) {

                  return (
                    rankA -
                    rankB
                  )

                }

                /*
                |--------------------------------------------------------------------------
                | A Ranked
                |--------------------------------------------------------------------------
                */

                if (
                  rankA
                ) {

                  return -1

                }

                /*
                |--------------------------------------------------------------------------
                | B Ranked
                |--------------------------------------------------------------------------
                */

                if (
                  rankB
                ) {

                  return 1

                }

                /*
                |--------------------------------------------------------------------------
                | Remaining Candidates
                |--------------------------------------------------------------------------
                */

                return (
                  a
                    ?.candidateName ||
                  ""
                )
                  .localeCompare(
                    b
                      ?.candidateName ||
                    ""
                  )

              }
            )

        },
        [
          candidates,
        ]
      )


    /*
    |--------------------------------------------------------------------------
    | 06 - Top 3
    |--------------------------------------------------------------------------
    */

    const topSubmittedCandidates =
      useMemo(
        () => {

          return submittedCandidates
            .slice(
              0,
              3
            )

        },
        [
          submittedCandidates,
        ]
      )


    /*
    |--------------------------------------------------------------------------
    | 07 - Visible Candidates
    |--------------------------------------------------------------------------
    */

    const visibleResponsivenessCandidates =
      showAllCandidates
        ? responsivenessCandidates
        : topSubmittedCandidates


    /*
    |--------------------------------------------------------------------------
    | 08 - Selected Candidate
    |--------------------------------------------------------------------------
    */

    const selectedInsightCandidate =
      useMemo(
        () => {

          if (
            !selectedInsightCandidateId
          ) {

            return null

          }

          return (
            responsivenessCandidates
              .find(
                candidate =>
                  candidate
                    ?.intentionCandidateId ===
                  selectedInsightCandidateId
              ) ||
            null
          )

        },
        [
          responsivenessCandidates,
          selectedInsightCandidateId,
        ]
      )


    /*
    |--------------------------------------------------------------------------
    | 09 - Selected Question
    |--------------------------------------------------------------------------
    */

    const selectedQuestion =
      useMemo(
        () => {

          if (
            questions.length ===
            0
          ) {

            return null

          }

          if (
            selectedQuestionId
          ) {

            return (
              questions.find(
                question =>
                  question
                    ?.intentionQuestionId ===
                  selectedQuestionId
              ) ||
              questions[0]
            )

          }

          return questions[0]

        },
        [
          questions,
          selectedQuestionId,
        ]
      )


    /*
    |--------------------------------------------------------------------------
    | 10 - Format Minutes
    |--------------------------------------------------------------------------
    */

    const formatMinutes =
      minutes => {

        if (
          minutes ===
            null ||
          minutes ===
            undefined
        ) {

          return "—"

        }

        const numericMinutes =
          Number(
            minutes
          )

        if (
          Number.isNaN(
            numericMinutes
          )
        ) {

          return "—"

        }

        /*
        |--------------------------------------------------------------------------
        | Genuine Zero
        |--------------------------------------------------------------------------
        */

        if (
          numericMinutes ===
          0
        ) {

          return "0 min"

        }

        /*
        |--------------------------------------------------------------------------
        | Positive Sub Minute
        |--------------------------------------------------------------------------
        */

        if (
          numericMinutes >
          0 &&
          numericMinutes <
          1
        ) {

          return "< 1 min"

        }

        /*
        |--------------------------------------------------------------------------
        | Negative Sub Minute
        |--------------------------------------------------------------------------
        */

        if (
          numericMinutes <
          0 &&
          numericMinutes >
          -1
        ) {

          return "< 1 min late"

        }

        /*
        |--------------------------------------------------------------------------
        | Negative Duration
        |--------------------------------------------------------------------------
        */

        if (
          numericMinutes <
          0
        ) {

          const absoluteMinutes =
            Math.abs(
              numericMinutes
            )

          if (
            absoluteMinutes <
            60
          ) {

            return `${Math.round(
              absoluteMinutes
            )} min late`

          }

          const roundedAbsoluteMinutes =
            Math.round(
              absoluteMinutes
            )

          const lateHours =
            Math.floor(
              roundedAbsoluteMinutes /
              60
            )

          const lateMinutes =
            roundedAbsoluteMinutes %
            60

          if (
            lateHours <
            24
          ) {

            return lateMinutes
              ? `${lateHours}h ${lateMinutes}m late`
              : `${lateHours}h late`

          }

          const lateDays =
            Math.floor(
              lateHours /
              24
            )

          const remainingLateHours =
            lateHours %
            24

          return remainingLateHours
            ? `${lateDays}d ${remainingLateHours}h late`
            : `${lateDays}d late`

        }

        /*
        |--------------------------------------------------------------------------
        | Under One Hour
        |--------------------------------------------------------------------------
        */

        if (
          numericMinutes <
          60
        ) {

          return `${Math.round(
            numericMinutes
          )} min`

        }

        const roundedMinutes =
          Math.round(
            numericMinutes
          )

        const hours =
          Math.floor(
            roundedMinutes /
            60
          )

        const remainingMinutes =
          roundedMinutes %
          60

        /*
        |--------------------------------------------------------------------------
        | Under One Day
        |--------------------------------------------------------------------------
        */

        if (
          hours <
          24
        ) {

          return remainingMinutes
            ? `${hours}h ${remainingMinutes}m`
            : `${hours}h`

        }

        /*
        |--------------------------------------------------------------------------
        | Days
        |--------------------------------------------------------------------------
        */

        const days =
          Math.floor(
            hours /
            24
          )

        const remainingHours =
          hours %
          24

        return remainingHours
          ? `${days}d ${remainingHours}h`
          : `${days}d`

      }


    /*
    |--------------------------------------------------------------------------
    | 11 - Candidate Status
    |--------------------------------------------------------------------------
    */

    const getCandidateStatusLabel =
      status => {

        const statusMap = {

          nominated:
            "Nominated",

          invited:
            "Invited",

          accessed:
            "Accessed",

          draft:
            "In Progress",

          submitted:
            "Submitted",

          expired:
            "Expired",

          withdrawn:
            "Withdrawn",

        }

        return (
          statusMap[
            status
          ] ||
          status ||
          "Unknown"
        )

      }


    const getCandidateStatusClass =
      status => {

        switch (
          status
        ) {

          case "submitted":

            return "bg-lime-100 text-lime-700"

          case "draft":

            return "bg-amber-100 text-amber-700"

          case "accessed":

            return "bg-sky-100 text-sky-700"

          case "withdrawn":

            return "bg-red-100 text-red-700"

          case "expired":

            return "bg-gray-200 text-gray-600"

          default:

            return "bg-gray-100 text-gray-600"

        }

      }


    /*
    |--------------------------------------------------------------------------
    | 12 - Submission Rate
    |--------------------------------------------------------------------------
    */

    const submissionRate =
      Number(
        summary
          ?.submissionRate ||
        0
      )


    /*
    |--------------------------------------------------------------------------
    | 13 - Loading
    |--------------------------------------------------------------------------
    */

    if (
      isLoading
    ) {

      return (

        <div className="bg-white rounded-xl m-1 p-8">

          <div className="flex justify-center items-center">

            <PulseLoader
              color={"#ea580c"}
              size={7}
            />

          </div>

        </div>

      )

    }


    /*
    |--------------------------------------------------------------------------
    | 14 - Error
    |--------------------------------------------------------------------------
    */

    if (
      isError
    ) {

      return (

        <div className="bg-orange-50 border border-orange-100 rounded-xl m-1 p-5">

          <p className="text-xs font-bold text-orange-700">
            Unable to load Response Insights
          </p>

          <p className="text-[10px] text-orange-600 mt-1">

            {
              error
                ?.data
                ?.message ||
              "Please try again."
            }

          </p>

        </div>

      )

    }


    /*
    |--------------------------------------------------------------------------
    | 15 - No Data
    |--------------------------------------------------------------------------
    */

    if (
      !responseInsights
    ) {

      return null

    }


    /*
    |--------------------------------------------------------------------------
    | 16 - Render
    |--------------------------------------------------------------------------
    */

    return (

      <div className="w-full">

        {/*
        |--------------------------------------------------------------------------
        | Header
        |--------------------------------------------------------------------------
        */}

        <div className="m-1 rounded-xl overflow-hidden border border-gray-200 bg-white">

          <div className="px-5 py-5 bg-gradient-to-r from-orange-50 via-white to-white">

            <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center gap-4">

              <div className="flex justify-start items-center gap-3">

                <div className="w-10 h-10 rounded-xl bg-orange-600 flex justify-center items-center shrink-0">

                  <ChartBarIcon className="w-5 h-5 text-white" />

                </div>

                <div>

                  <p className="text-base font-bold text-gray-900">
                    Response Insights
                  </p>

                  <p className="text-[10px] text-gray-500 mt-1">
                    Sensing responsiveness, commitment and patterns across candidate responses
                  </p>

                </div>

              </div>


              {isFetching && (

                <div className="flex justify-start items-center gap-2">

                  <PulseLoader
                    color={"#ea580c"}
                    size={4}
                  />

                  <p className="text-[9px] text-gray-400">
                    Refreshing
                  </p>

                </div>

              )}

            </div>

          </div>


          {/*
          |--------------------------------------------------------------------------
          | Sub Tabs
          |--------------------------------------------------------------------------
          */}

          <div className="px-4 pb-4">

            <div className="inline-flex bg-gray-100 rounded-xl p-1">

              <button
                type="button"
                onClick={
                  () =>
                    setActiveView(
                      "overview"
                    )
                }
                className={`flex justify-center items-center gap-2 rounded-lg px-4 py-2.5 text-[10px] font-bold ${
                  activeView ===
                  "overview"
                    ? "bg-white text-orange-700 shadow-sm"
                    : "text-gray-500"
                }`}
              >

                <ChartBarIcon className="w-4 h-4" />

                Overview

              </button>

              <button
                type="button"
                onClick={
                  () =>
                    setActiveView(
                      "candidates"
                    )
                }
                className={`flex justify-center items-center gap-2 rounded-lg px-4 py-2.5 text-[10px] font-bold ${
                  activeView ===
                  "candidates"
                    ? "bg-white text-orange-700 shadow-sm"
                    : "text-gray-500"
                }`}
              >

                <UserGroupIcon className="w-4 h-4" />

                Candidates

              </button>

              <button
                type="button"
                onClick={
                  () =>
                    setActiveView(
                      "questions"
                    )
                }
                className={`flex justify-center items-center gap-2 rounded-lg px-4 py-2.5 text-[10px] font-bold ${
                  activeView ===
                  "questions"
                    ? "bg-white text-orange-700 shadow-sm"
                    : "text-gray-500"
                }`}
              >

                <QuestionMarkCircleIcon className="w-4 h-4" />

                Questions

              </button>

            </div>

          </div>

        </div>


        {/*
        |--------------------------------------------------------------------------
        | Overview
        |--------------------------------------------------------------------------
        */}

        {activeView ===
          "overview" && (

          <div>

            {/*
            |--------------------------------------------------------------------------
            | Primary Metrics
            |--------------------------------------------------------------------------
            */}

            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-2 m-1 mt-2">

              <div className="relative overflow-hidden bg-white border border-gray-200 rounded-xl p-4">

                <div className="absolute top-0 left-0 w-1 h-full bg-gray-300" />

                <div className="flex justify-between items-start">

                  <div>

                    <p className="text-[9px] font-bold uppercase tracking-wide text-gray-400">
                      Candidates
                    </p>

                    <p className="text-2xl font-bold text-gray-900 mt-2">
                      {
                        summary
                          ?.totalCandidates ??
                        0
                      }
                    </p>

                    <p className="text-[9px] text-gray-500 mt-1">
                      nominated to participate
                    </p>

                  </div>

                  <div className="w-9 h-9 rounded-lg bg-gray-100 flex justify-center items-center">

                    <UserGroupIcon className="w-4 h-4 text-gray-600" />

                  </div>

                </div>

              </div>


              <div className="relative overflow-hidden bg-white border border-gray-200 rounded-xl p-4">

                <div className="absolute top-0 left-0 w-1 h-full bg-lime-500" />

                <div className="flex justify-between items-start">

                  <div>

                    <p className="text-[9px] font-bold uppercase tracking-wide text-gray-400">
                      Submission Rate
                    </p>

                    <p className="text-2xl font-bold text-gray-900 mt-2">
                      {submissionRate}%
                    </p>

                    <p className="text-[9px] text-gray-500 mt-1">

                      {
                        summary
                          ?.submittedCandidates ??
                        0
                      }
                      {" of "}
                      {
                        summary
                          ?.totalCandidates ??
                        0
                      }
                      {" submitted"}

                    </p>

                  </div>

                  <div className="relative w-11 h-11">

                    <svg
                      viewBox="0 0 40 40"
                      className="w-full h-full -rotate-90"
                    >

                      <circle
                        cx="20"
                        cy="20"
                        r="16"
                        fill="none"
                        stroke="#e5e7eb"
                        strokeWidth="4"
                      />

                      <circle
                        cx="20"
                        cy="20"
                        r="16"
                        fill="none"
                        stroke="#84cc16"
                        strokeWidth="4"
                        strokeLinecap="round"
                        pathLength="100"
                        strokeDasharray={
                          `${submissionRate} ${
                            100 -
                            submissionRate
                          }`
                        }
                      />

                    </svg>

                  </div>

                </div>

              </div>


              <div className="relative overflow-hidden bg-white border border-gray-200 rounded-xl p-4">

                <div className="absolute top-0 left-0 w-1 h-full bg-orange-500" />

                <div className="flex justify-between items-start">

                  <div>

                    <p className="text-[9px] font-bold uppercase tracking-wide text-gray-400">
                      Median Completion
                    </p>

                    <p className="text-lg font-bold text-gray-900 mt-2">
                      {
                        formatMinutes(
                          summary
                            ?.medianStartToSubmitMinutes
                        )
                      }
                    </p>

                    <p className="text-[9px] text-gray-500 mt-1">
                      start to submission
                    </p>

                  </div>

                  <div className="w-9 h-9 rounded-lg bg-orange-50 flex justify-center items-center">

                    <ClockIcon className="w-4 h-4 text-orange-600" />

                  </div>

                </div>

              </div>


              <div className="relative overflow-hidden bg-white border border-gray-200 rounded-xl p-4">

                <div className="absolute top-0 left-0 w-1 h-full bg-sky-500" />

                <div className="flex justify-between items-start">

                  <div>

                    <p className="text-[9px] font-bold uppercase tracking-wide text-gray-400">
                      Median Deadline Lead
                    </p>

                    <p className="text-lg font-bold text-gray-900 mt-2">
                      {
                        formatMinutes(
                          summary
                            ?.medianDeadlineLeadMinutes
                        )
                      }
                    </p>

                    <p className="text-[9px] text-gray-500 mt-1">
                      before response deadline
                    </p>

                  </div>

                  <div className="w-9 h-9 rounded-lg bg-sky-50 flex justify-center items-center">

                    <CalendarDaysIcon className="w-4 h-4 text-sky-600" />

                  </div>

                </div>

              </div>

            </div>


            {/*
            |--------------------------------------------------------------------------
            | First Submission / Fastest Completion
            |--------------------------------------------------------------------------
            */}

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-2 m-1 mt-2">

              <div className="bg-white border border-gray-200 rounded-xl p-5">

                <div className="flex items-center gap-2">

                  <CheckCircleIcon className="w-4 h-4 text-orange-600" />

                  <p className="text-xs font-bold text-gray-800">
                    First Complete Submission
                  </p>

                </div>

                {
                  summary
                    ?.firstCompleteSubmission ? (

                    <div className="mt-4">

                      <p className="text-lg font-bold text-orange-700">
                        #1
                      </p>

                      <p className="text-base font-bold text-gray-900 mt-1">
                        {
                          summary
                            .firstCompleteSubmission
                            .candidateName
                        }
                      </p>

                    </div>

                  ) : (

                    <p className="text-xs text-gray-400 mt-4">
                      No completed responses yet.
                    </p>

                  )
                }

              </div>


              <div className="bg-white border border-gray-200 rounded-xl p-5">

                <div className="flex items-center gap-2">

                  <BoltIcon className="w-4 h-4 text-orange-600" />

                  <p className="text-xs font-bold text-gray-800">
                    Fastest Completion
                  </p>

                </div>

                {
                  summary
                    ?.fastestCompletion ? (

                    <div className="mt-4">

                      <p className="text-base font-bold text-gray-900">
                        {
                          summary
                            .fastestCompletion
                            .candidateName
                        }
                      </p>

                      <p className="text-lg font-bold text-orange-700 mt-1">
                        {
                          formatMinutes(
                            summary
                              .fastestCompletion
                              .startToSubmitMinutes
                          )
                        }
                      </p>

                    </div>

                  ) : (

                    <p className="text-xs text-gray-400 mt-4">
                      No completed responses yet.
                    </p>

                  )
                }

              </div>

            </div>


            {/*
            |--------------------------------------------------------------------------
            | Submission Posture
            |--------------------------------------------------------------------------
            */}

            <div className="bg-white border border-gray-200 rounded-xl m-1 mt-2 overflow-hidden">

              <div className="p-4 border-b border-gray-100">

                <div className="flex justify-start items-center gap-2">

                  <CheckCircleIcon className="w-4 h-4 text-lime-600" />

                  <div>

                    <p className="text-xs font-bold text-gray-800">
                      Submission Posture
                    </p>

                    <p className="text-[9px] text-gray-400 mt-0.5">
                      How completed responses reached final submission
                    </p>

                  </div>

                </div>

              </div>

              <div className="grid grid-cols-2 gap-2 p-4">

                <div className="rounded-xl bg-lime-50 p-4">

                  <p className="text-[9px] font-bold uppercase text-lime-700">
                    Manual
                  </p>

                  <p className="text-2xl font-bold text-gray-900 mt-2">
                    {
                      summary
                        ?.manualSubmissions ??
                      0
                    }
                  </p>

                  <p className="text-[9px] text-gray-500 mt-1">
                    actively submitted
                  </p>

                </div>

                <div className="rounded-xl bg-gray-100 p-4">

                  <p className="text-[9px] font-bold uppercase text-gray-600">
                    Deadline Auto Submit
                  </p>

                  <p className="text-2xl font-bold text-gray-900 mt-2">
                    {
                      summary
                        ?.automaticDeadlineSubmissions ??
                      0
                    }
                  </p>

                  <p className="text-[9px] text-gray-500 mt-1">
                    finalized at deadline
                  </p>

                </div>

              </div>

            </div>


            {/*
            |--------------------------------------------------------------------------
            | Cohort Responsiveness
            |--------------------------------------------------------------------------
            */}

            <div className="bg-white border border-gray-200 rounded-xl m-1 mt-2 overflow-hidden">

              <div className="p-4 border-b border-gray-100">

                <div className="flex items-center gap-2">

                  <ArrowTrendingUpIcon className="w-4 h-4 text-orange-600" />

                  <div>

                    <p className="text-xs font-bold text-gray-800">
                      Cohort Responsiveness
                    </p>

                    <p className="text-[9px] text-gray-400 mt-0.5">
                      Timing patterns across the candidate journey
                    </p>

                  </div>

                </div>

              </div>

              <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-gray-100">

                <div className="bg-white p-4">

                  <p className="text-[8px] uppercase text-gray-400">
                    Avg Nomination → Access
                  </p>

                  <p className="text-sm font-bold mt-2">
                    {
                      formatMinutes(
                        summary
                          ?.averageNominationToAccessMinutes
                      )
                    }
                  </p>

                </div>

                <div className="bg-white p-4">

                  <p className="text-[8px] uppercase text-gray-400">
                    Median Nomination → Access
                  </p>

                  <p className="text-sm font-bold mt-2">
                    {
                      formatMinutes(
                        summary
                          ?.medianNominationToAccessMinutes
                      )
                    }
                  </p>

                </div>

                <div className="bg-white p-4">

                  <p className="text-[8px] uppercase text-gray-400">
                    Avg Start → Submit
                  </p>

                  <p className="text-sm font-bold mt-2">
                    {
                      formatMinutes(
                        summary
                          ?.averageStartToSubmitMinutes
                      )
                    }
                  </p>

                </div>

                <div className="bg-white p-4">

                  <p className="text-[8px] uppercase text-gray-400">
                    Avg Deadline Lead
                  </p>

                  <p className="text-sm font-bold mt-2">
                    {
                      formatMinutes(
                        summary
                          ?.averageDeadlineLeadMinutes
                      )
                    }
                  </p>

                </div>

              </div>

            </div>

          </div>

        )}


        {/*
        |--------------------------------------------------------------------------
        | Candidates
        |--------------------------------------------------------------------------
        */}

        {activeView ===
          "candidates" && (

          <div className="m-1 mt-2">

            {/*
            |--------------------------------------------------------------------------
            | Candidate Board Header
            |--------------------------------------------------------------------------
            */}

            <div className="bg-white border border-gray-200 rounded-xl p-4">

              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">

                <div>

                  <div className="flex justify-start items-center gap-2">

                    <BoltIcon className="w-4 h-4 text-orange-600" />

                    <p className="text-xs font-bold text-gray-800">
                      Candidate Responsiveness
                    </p>

                  </div>

                  <p className="text-[9px] text-gray-400 mt-1">
                    Rank recognizes the order of complete submissions. Select a candidate for detail.
                  </p>

                </div>


                {responsivenessCandidates.length >
                  topSubmittedCandidates.length && (

                  <div className="inline-flex bg-gray-100 rounded-lg p-1 self-start sm:self-auto">

                    <button
                      type="button"
                      onClick={
                        () =>
                          setShowAllCandidates(
                            false
                          )
                      }
                      className={`rounded-md px-3 py-1.5 text-[9px] font-bold ${
                        !showAllCandidates
                          ? "bg-white text-orange-700 shadow-sm"
                          : "text-gray-500"
                      }`}
                    >
                      Top 3
                    </button>

                    <button
                      type="button"
                      onClick={
                        () =>
                          setShowAllCandidates(
                            true
                          )
                      }
                      className={`rounded-md px-3 py-1.5 text-[9px] font-bold ${
                        showAllCandidates
                          ? "bg-white text-orange-700 shadow-sm"
                          : "text-gray-500"
                      }`}
                    >
                      Show All
                    </button>

                  </div>

                )}

              </div>

            </div>


            {/*
            |--------------------------------------------------------------------------
            | Candidate Tile Board
            |--------------------------------------------------------------------------
            */}

            <div className="bg-white border border-gray-200 rounded-xl p-4 mt-2">

              {visibleResponsivenessCandidates.length ===
                0 && (

                <div className="py-8 text-center">

                  <p className="text-xs font-bold text-gray-600">
                    No completed submissions yet
                  </p>

                  <p className="text-[9px] text-gray-400 mt-1">
                    Candidate ranks will appear as complete responses are submitted.
                  </p>

                  {!showAllCandidates &&
                    responsivenessCandidates.length >
                    0 && (

                    <button
                      type="button"
                      onClick={
                        () =>
                          setShowAllCandidates(
                            true
                          )
                      }
                      className="mt-3 text-[9px] font-bold text-orange-600"
                    >
                      Show All Candidates
                    </button>

                  )}

                </div>

              )}


              {visibleResponsivenessCandidates.length >
                0 && (

                <div
                  className="
                    grid
                    grid-cols-2
                    sm:grid-cols-3
                    md:grid-cols-4
                    lg:grid-cols-5
                    xl:grid-cols-6
                    gap-3
                  "
                >

                  {
                    visibleResponsivenessCandidates
                      .map(
                        candidate => {

                          const rank =
                            candidate
                              ?.responsiveness
                              ?.submissionRank

                          const isSubmitted =
                            Boolean(
                              rank
                            )

                          const isSelected =
                            selectedInsightCandidateId ===
                            candidate
                              ?.intentionCandidateId

                          return (

                            <button
                              key={
                                candidate
                                  .intentionCandidateId
                              }
                              type="button"
                              onClick={
                                () =>
                                  setSelectedInsightCandidateId(
                                    candidate
                                      .intentionCandidateId
                                  )
                              }
                              className={`
                                relative
                                min-h-[118px]
                                rounded-xl
                                border
                                px-3
                                py-4
                                transition-all
                                flex
                                flex-col
                                justify-center
                                items-center
                                text-center

                                ${
                                  isSubmitted
                                    ? "bg-lime-50 border-lime-200 hover:bg-lime-100"
                                    : "bg-gray-50 border-gray-200 hover:bg-gray-100"
                                }

                                ${
                                  isSelected
                                    ? "ring-2 ring-orange-300 border-orange-400 shadow-sm"
                                    : ""
                                }
                              `}
                            >

                              {/*
                              |--------------------------------------------------------------------------
                              | Rank
                              |--------------------------------------------------------------------------
                              */}

                              {rank && (

                                <div
                                  className={`
                                    absolute
                                    top-2
                                    left-2
                                    min-w-[28px]
                                    rounded-full
                                    px-2
                                    py-1
                                    text-[9px]
                                    font-bold

                                    ${
                                      rank ===
                                      1
                                        ? "bg-orange-600 text-white"
                                        : rank ===
                                          2
                                          ? "bg-orange-100 text-orange-700"
                                          : rank ===
                                            3
                                            ? "bg-white text-lime-700 border border-lime-200"
                                            : "bg-white text-gray-500 border border-gray-200"
                                    }
                                  `}
                                >
                                  #{rank}
                                </div>

                              )}


                              {/*
                              |--------------------------------------------------------------------------
                              | Not Submitted Badge
                              |--------------------------------------------------------------------------
                              */}

                              {!isSubmitted && (

                                <div
                                  className="
                                    absolute
                                    top-2
                                    left-1/2
                                    -translate-x-1/2
                                    rounded-full
                                    bg-red-200
                                    px-2.5
                                    py-1
                                    whitespace-nowrap
                                  "
                                >

                                  <p className="text-[8px] font-bold text-red-700">
                                    Not Submitted
                                  </p>

                                </div>

                              )}


                              {/*
                              |--------------------------------------------------------------------------
                              | User Icon
                              |--------------------------------------------------------------------------
                              */}

                              <div
                                className={`
                                  w-9
                                  h-9
                                  rounded-full
                                  flex
                                  justify-center
                                  items-center

                                  ${
                                    isSubmitted
                                      ? "bg-lime-100"
                                      : "bg-white"
                                  }
                                `}
                              >

                                <UserIcon
                                  className={`
                                    w-4
                                    h-4

                                    ${
                                      isSubmitted
                                        ? "text-lime-700"
                                        : "text-gray-500"
                                    }
                                  `}
                                />

                              </div>


                              {/*
                              |--------------------------------------------------------------------------
                              | Candidate Name
                              |--------------------------------------------------------------------------
                              */}

                              <p
                                className={`
                                  text-[10px]
                                  font-bold
                                  leading-4
                                  mt-2
                                  line-clamp-2

                                  ${
                                    isSubmitted
                                      ? "text-lime-900"
                                      : "text-gray-700"
                                  }
                                `}
                              >
                                {
                                  candidate
                                    ?.candidateName
                                }
                              </p>

                            </button>

                          )

                        }
                      )
                  }

                </div>

              )}

            </div>


            {/*
            |--------------------------------------------------------------------------
            | Selected Candidate Detail
            |--------------------------------------------------------------------------
            */}

            {selectedInsightCandidate && (

              <div className="bg-white border border-gray-200 rounded-xl overflow-hidden mt-2">

                {/*
                |--------------------------------------------------------------------------
                | Candidate Detail Header
                |--------------------------------------------------------------------------
                */}

                <div className="p-4 border-b border-gray-100">

                  <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-3">

                    <div className="flex items-center gap-3 min-w-0">

                      <div
                        className={`w-10 h-10 rounded-full flex justify-center items-center ${
                          selectedInsightCandidate
                            ?.responsiveness
                            ?.submissionRank
                            ? "bg-lime-100"
                            : "bg-gray-100"
                        }`}
                      >

                        <UserIcon
                          className={`w-5 h-5 ${
                            selectedInsightCandidate
                              ?.responsiveness
                              ?.submissionRank
                              ? "text-lime-700"
                              : "text-gray-500"
                          }`}
                        />

                      </div>

                      <div className="min-w-0">

                        <p className="text-sm font-bold text-gray-900 truncate">
                          {
                            selectedInsightCandidate
                              ?.candidateName
                          }
                        </p>

                        <p className="text-[9px] text-gray-400 mt-1 truncate">
                          {
                            selectedInsightCandidate
                              ?.candidateEmail
                          }
                        </p>

                      </div>

                    </div>


                    <div className="flex items-center flex-wrap gap-1">

                      {
                        selectedInsightCandidate
                          ?.responsiveness
                          ?.submissionRank && (

                          <div className="rounded-full bg-lime-50 border border-lime-200 px-3 py-1">

                            <p className="text-[9px] font-bold text-lime-700">
                              Submitted #
                              {
                                selectedInsightCandidate
                                  .responsiveness
                                  .submissionRank
                              }
                            </p>

                          </div>

                        )
                      }

                      {
                        selectedInsightCandidate
                          ?.responsiveness
                          ?.completionSpeedRank && (

                          <div className="rounded-full bg-gray-100 px-3 py-1">

                            <p className="text-[9px] font-bold text-gray-600">
                              Completion Speed #
                              {
                                selectedInsightCandidate
                                  .responsiveness
                                  .completionSpeedRank
                              }
                            </p>

                          </div>

                        )
                      }

                      <div
                        className={`rounded-full px-3 py-1 text-[9px] font-bold ${
                          getCandidateStatusClass(
                            selectedInsightCandidate
                              ?.candidateStatus
                          )
                        }`}
                      >

                        {
                          getCandidateStatusLabel(
                            selectedInsightCandidate
                              ?.candidateStatus
                          )
                        }

                      </div>

                    </div>

                  </div>

                </div>


                {/*
                |--------------------------------------------------------------------------
                | Candidate Detail Body
                |--------------------------------------------------------------------------
                */}

                <div className="grid grid-cols-1 xl:grid-cols-12">

                  {/*
                  |--------------------------------------------------------------------------
                  | Responsiveness
                  |--------------------------------------------------------------------------
                  */}

                  <div className="xl:col-span-7 p-4 xl:border-r border-gray-100">

                    <div className="flex items-center gap-2 mb-3">

                      <ClockIcon className="w-4 h-4 text-orange-600" />

                      <div>

                        <p className="text-[9px] font-bold uppercase text-gray-500">
                          Responsiveness
                        </p>

                        <p className="text-[8px] text-gray-400">
                          How the candidate moved through the response journey
                        </p>

                      </div>

                    </div>


                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">

                      <div className="bg-gray-50 rounded-xl p-3">

                        <p className="text-[8px] font-bold text-gray-400 uppercase">
                          Nomination
                        </p>

                        <p className="text-[8px] text-gray-400">
                          to Access
                        </p>

                        <p className="text-sm font-bold mt-3">
                          {
                            formatMinutes(
                              selectedInsightCandidate
                                ?.responsiveness
                                ?.nominationToAccessMinutes
                            )
                          }
                        </p>

                      </div>


                      <div className="bg-gray-50 rounded-xl p-3">

                        <p className="text-[8px] font-bold text-gray-400 uppercase">
                          Access
                        </p>

                        <p className="text-[8px] text-gray-400">
                          to Start
                        </p>

                        <p className="text-sm font-bold mt-3">
                          {
                            formatMinutes(
                              selectedInsightCandidate
                                ?.responsiveness
                                ?.accessToStartMinutes
                            )
                          }
                        </p>

                      </div>


                      <div className="bg-orange-50 rounded-xl p-3">

                        <p className="text-[8px] font-bold text-orange-600 uppercase">
                          Start
                        </p>

                        <p className="text-[8px] text-orange-500">
                          to Submit
                        </p>

                        <p className="text-sm font-bold text-orange-800 mt-3">
                          {
                            formatMinutes(
                              selectedInsightCandidate
                                ?.responsiveness
                                ?.startToSubmitMinutes
                            )
                          }
                        </p>

                      </div>


                      <div className="bg-sky-50 rounded-xl p-3">

                        <p className="text-[8px] font-bold text-sky-600 uppercase">
                          Deadline
                        </p>

                        <p className="text-[8px] text-sky-500">
                          Lead
                        </p>

                        <p className="text-sm font-bold text-sky-800 mt-3">
                          {
                            formatMinutes(
                              selectedInsightCandidate
                                ?.responsiveness
                                ?.deadlineLeadMinutes
                            )
                          }
                        </p>

                      </div>

                    </div>


                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-2">

                      <div className="border border-gray-100 rounded-lg p-3">

                        <p className="text-[8px] text-gray-400">
                          Nomination → Start
                        </p>

                        <p className="text-[10px] font-bold text-gray-700 mt-1">
                          {
                            formatMinutes(
                              selectedInsightCandidate
                                ?.responsiveness
                                ?.nominationToStartMinutes
                            )
                          }
                        </p>

                      </div>

                      <div className="border border-gray-100 rounded-lg p-3">

                        <p className="text-[8px] text-gray-400">
                          Nomination → Submission
                        </p>

                        <p className="text-[10px] font-bold text-gray-700 mt-1">
                          {
                            formatMinutes(
                              selectedInsightCandidate
                                ?.responsiveness
                                ?.nominationToSubmitMinutes
                            )
                          }
                        </p>

                      </div>

                    </div>

                  </div>


                  {/*
                  |--------------------------------------------------------------------------
                  | Commitment Signals
                  |--------------------------------------------------------------------------
                  */}

                  <div className="xl:col-span-5 p-4">

                    <div className="flex items-center gap-2 mb-3">

                      <CheckCircleIcon className="w-4 h-4 text-lime-600" />

                      <div>

                        <p className="text-[9px] font-bold uppercase text-gray-500">
                          Commitment Signals
                        </p>

                        <p className="text-[8px] text-gray-400">
                          Stated readiness and response posture
                        </p>

                      </div>

                    </div>


                    <div className="space-y-2">

                      <div className="bg-gray-50 rounded-xl p-3">

                        <p className="text-[8px] font-bold uppercase text-gray-400">
                          Participation Intent
                        </p>

                        <p className="text-[10px] font-bold text-gray-800 mt-2">
                          {
                            selectedInsightCandidate
                              ?.commitmentSignals
                              ?.participationIntent
                              ?.selectedLabel ||
                            "Not submitted"
                          }
                        </p>

                      </div>


                      <div className="bg-gray-50 rounded-xl p-3">

                        <p className="text-[8px] font-bold uppercase text-gray-400">
                          In Person Confirmation
                        </p>

                        <p className="text-[10px] font-bold text-gray-800 mt-2">
                          {
                            selectedInsightCandidate
                              ?.commitmentSignals
                              ?.inPersonConfirmation
                              ?.selectedLabel ||
                            "Not submitted"
                          }
                        </p>

                      </div>


                      <div className="grid grid-cols-2 gap-2">

                        <div className="border border-gray-100 rounded-lg p-3">

                          <p className="text-[8px] text-gray-400">
                            Submission
                          </p>

                          <p className="text-[10px] font-bold text-gray-700 mt-1">

                            {
                              selectedInsightCandidate
                                ?.commitmentSignals
                                ?.manualSubmission
                                ? "Manual"
                                : selectedInsightCandidate
                                    ?.commitmentSignals
                                    ?.automaticDeadlineSubmission
                                  ? "Automatic"
                                  : "—"
                            }

                          </p>

                        </div>

                        <div className="border border-gray-100 rounded-lg p-3">

                          <p className="text-[8px] text-gray-400">
                            Before Deadline
                          </p>

                          <p className="text-[10px] font-bold text-gray-700 mt-1">

                            {
                              selectedInsightCandidate
                                ?.hasSubmittedResponse
                                ? selectedInsightCandidate
                                    ?.commitmentSignals
                                    ?.completedBeforeDeadline
                                  ? "Yes"
                                  : "No"
                                : "—"
                            }

                          </p>

                        </div>

                      </div>

                    </div>

                  </div>

                </div>

              </div>

            )}

          </div>

        )}


        {/*
        |--------------------------------------------------------------------------
        | Questions
        |--------------------------------------------------------------------------
        */}

        {activeView ===
          "questions" && (

          <div
            className="
              grid
              grid-cols-1
              lg:grid-cols-12
              gap-2
              m-1
              mt-2
              lg:h-[calc(100vh-250px)]
              lg:overflow-hidden
            "
          >

            {/*
            |--------------------------------------------------------------------------
            | Question Navigator
            |--------------------------------------------------------------------------
            |
            | On large screens this remains fixed in view.
            |
            | Its own list can scroll if there are enough questions to exceed
            | the viewport height.
            |
            */}

            <div
              className="
                lg:col-span-4
                lg:h-full
                lg:min-h-0
              "
            >

              <div
                className="
                  bg-white
                  border
                  border-gray-200
                  rounded-xl
                  overflow-hidden
                  lg:h-full
                  lg:flex
                  lg:flex-col
                "
              >

                <div className="p-4 border-b border-gray-100 shrink-0">

                  <p className="text-xs font-bold text-gray-800">
                    Questions
                  </p>

                  <p className="text-[9px] text-gray-400 mt-1">
                    Compare responses across submitted candidates
                  </p>

                </div>


                <div
                  className="
                    p-2
                    space-y-1
                    lg:flex-1
                    lg:min-h-0
                    lg:overflow-y-auto
                  "
                >

                  {
                    questions.map(
                      question => {

                        const isSelected =
                          selectedQuestion
                            ?.intentionQuestionId ===
                          question
                            ?.intentionQuestionId

                        return (

                          <button
                            key={
                              question
                                .intentionQuestionId
                            }
                            type="button"
                            onClick={
                                () => {

                                    setSelectedQuestionId(
                                    question
                                        .intentionQuestionId
                                    )

                                    questionResponsePaneRef
                                    .current
                                    ?.scrollTo({
                                        top:
                                        0,

                                        behavior:
                                        "smooth",
                                    })

                                }
                            }
                            className={`w-full text-left rounded-lg p-3 ${
                              isSelected
                                ? "bg-orange-50 border border-orange-100"
                                : "bg-white border border-transparent hover:bg-gray-50"
                            }`}
                          >

                            <div className="flex justify-start items-start gap-3">

                              <div
                                className={`w-7 h-7 rounded-lg flex justify-center items-center shrink-0 ${
                                  isSelected
                                    ? "bg-orange-600"
                                    : "bg-gray-100"
                                }`}
                              >

                                <p
                                  className={`text-[9px] font-bold ${
                                    isSelected
                                      ? "text-white"
                                      : "text-gray-500"
                                  }`}
                                >
                                  {
                                    question
                                      ?.questionNumber
                                  }
                                </p>

                              </div>

                              <div className="min-w-0">

                                <p
                                  className={`text-[10px] font-bold leading-4 ${
                                    isSelected
                                      ? "text-orange-800"
                                      : "text-gray-700"
                                  }`}
                                >
                                  {
                                    question
                                      ?.questionText
                                  }
                                </p>

                                <p className="text-[8px] text-gray-400 mt-1">
                                  {
                                    question
                                      ?.stats
                                      ?.responseCount ??
                                    0
                                  }
                                  {" responses · "}
                                  {
                                    question
                                      ?.stats
                                      ?.responseRate ??
                                    0
                                  }
                                  %
                                </p>

                              </div>

                            </div>

                          </button>

                        )

                      }
                    )
                  }

                </div>

              </div>

            </div>


            {/*
            |--------------------------------------------------------------------------
            | Selected Question / Response Pane
            |--------------------------------------------------------------------------
            |
            | Independent vertical scroll on large screens.
            |
            | This allows the question navigator to remain visible while reading
            | a long set of candidate responses.
            |
            */}

            <div
              ref={
                    questionResponsePaneRef
                }
              className="
                lg:col-span-8
                lg:h-full
                lg:min-h-0
                lg:overflow-y-auto
                lg:pr-1
              "
            >

              {
                selectedQuestion && (

                  <div className="space-y-2">

                    {/*
                    |--------------------------------------------------------------------------
                    | Question Header
                    |--------------------------------------------------------------------------
                    */}

                    <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">

                      <div className="p-5">

                        <div className="flex justify-start items-start gap-3">

                          <div className="w-9 h-9 rounded-lg bg-orange-600 flex justify-center items-center shrink-0">

                            <p className="text-xs font-bold text-white">
                              {
                                selectedQuestion
                                  ?.questionNumber
                              }
                            </p>

                          </div>

                          <div>

                            <p className="text-sm font-bold text-gray-900 leading-5">
                              {
                                selectedQuestion
                                  ?.questionText
                              }
                            </p>

                            {
                              selectedQuestion
                                ?.questionDescription && (

                                <p className="text-[10px] text-gray-500 mt-1 leading-4">
                                  {
                                    selectedQuestion
                                      .questionDescription
                                  }
                                </p>

                              )
                            }

                          </div>

                        </div>

                      </div>


                      {/*
                      |--------------------------------------------------------------------------
                      | Question Metrics
                      |--------------------------------------------------------------------------
                      */}

                      <div className="grid grid-cols-3 gap-px bg-gray-100 border-t border-gray-100">

                        <div className="bg-white p-4">

                          <p className="text-[8px] font-bold uppercase text-gray-400">
                            Responses
                          </p>

                          <p className="text-lg font-bold text-gray-900 mt-1">
                            {
                              selectedQuestion
                                ?.stats
                                ?.responseCount ??
                              0
                            }
                          </p>

                        </div>

                        <div className="bg-white p-4">

                          <p className="text-[8px] font-bold uppercase text-gray-400">
                            Response Rate
                          </p>

                          <p className="text-lg font-bold text-lime-700 mt-1">
                            {
                              selectedQuestion
                                ?.stats
                                ?.responseRate ??
                              0
                            }
                            %
                          </p>

                        </div>

                        <div className="bg-white p-4">

                          <p className="text-[8px] font-bold uppercase text-gray-400">
                            No Response
                          </p>

                          <p className="text-lg font-bold text-gray-900 mt-1">
                            {
                              selectedQuestion
                                ?.stats
                                ?.noResponseCount ??
                              0
                            }
                          </p>

                        </div>

                      </div>

                    </div>


                    {/*
                    |--------------------------------------------------------------------------
                    | Option Distribution
                    |--------------------------------------------------------------------------
                    */}

                    {
                      selectedQuestion
                        ?.optionStats
                        ?.length >
                      0 && (

                        <div className="bg-white border border-gray-200 rounded-xl p-4">

                          <p className="text-xs font-bold text-gray-800">
                            Response Distribution
                          </p>

                          <p className="text-[9px] text-gray-400 mt-1">
                            Distribution across submitted candidate selections
                          </p>

                          <div className="space-y-3 mt-4">

                            {
                              selectedQuestion
                                .optionStats
                                .map(
                                  option => (

                                    <div
                                      key={
                                        option
                                          .optionValue
                                      }
                                    >

                                      <div className="flex justify-between gap-3">

                                        <p className="text-[10px] font-bold text-gray-700">
                                          {
                                            option
                                              .optionLabel
                                          }
                                        </p>

                                        <p className="text-[9px] font-bold text-gray-600 shrink-0">
                                          {
                                            option
                                              .count
                                          }
                                          {" · "}
                                          {
                                            option
                                              .percentage
                                          }
                                          %
                                        </p>

                                      </div>

                                      <div className="w-full h-1.5 bg-gray-100 rounded-full mt-2">

                                        <div
                                          className="h-full bg-orange-500 rounded-full"
                                          style={{
                                            width:
                                              `${option?.percentage || 0}%`,
                                          }}
                                        />

                                      </div>

                                    </div>

                                  )
                                )
                            }

                          </div>

                        </div>

                      )
                    }


                    {/*
                    |--------------------------------------------------------------------------
                    | Candidate Responses
                    |--------------------------------------------------------------------------
                    */}

                    <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">

                      <div className="p-4 border-b border-gray-100">

                        <p className="text-xs font-bold text-gray-800">
                          Candidate Responses
                        </p>

                        <p className="text-[9px] text-gray-400 mt-1">
                          Individual responses to this question
                        </p>

                      </div>

                      <div className="divide-y divide-gray-100">

                        {
                          selectedQuestion
                            ?.responses
                            ?.map(
                              response => (

                                <div
                                  key={
                                    response
                                      .intentionCandidateId
                                  }
                                  className="p-4"
                                >

                                  <div className="flex justify-start items-center gap-2">

                                    <div className="w-7 h-7 rounded-full bg-gray-100 flex justify-center items-center">

                                      <UserIcon className="w-3.5 h-3.5 text-gray-500" />

                                    </div>

                                    <p className="text-[10px] font-bold text-gray-800">
                                      {
                                        response
                                          ?.candidateName
                                      }
                                    </p>

                                  </div>


                                  {
                                    response
                                      ?.responseText ? (

                                      <div className="bg-gray-50 rounded-lg p-3 mt-3">

                                        <p className="text-xs text-gray-700 whitespace-pre-wrap leading-5">
                                          {
                                            response
                                              .responseText
                                          }
                                        </p>

                                      </div>

                                    ) : (

                                      <div className="mt-3 flex flex-wrap gap-2">

                                        {
                                          response
                                            ?.selectedOptionDetails
                                            ?.map(
                                              option => (

                                                <div
                                                  key={
                                                    option
                                                      .optionValue
                                                  }
                                                  className="bg-orange-50 border border-orange-100 rounded-lg px-3 py-2"
                                                >

                                                  <p className="text-[10px] font-bold text-orange-800">
                                                    {
                                                      option
                                                        .optionLabel
                                                    }
                                                  </p>

                                                </div>

                                              )
                                            )
                                        }

                                      </div>

                                    )
                                  }

                                </div>

                              )
                            )
                        }

                      </div>

                    </div>

                  </div>

                )
              }

            </div>

          </div>

        )}

      </div>

    )

  }


export default EtOrgIntentionResponseInsights